"use client";

import React, { useState } from "react";
import MortgagePieChart from "./MortgagePieChart";
import MortgageOverTimeChart from "./MortgageOverTimeChart";
import MortgageAmortizationTable from "./MortgageAmortizationTable";

/* ────────────────────────────────────────────────────────────────── */

interface MortgageResultProps {
    /** headline figure – “home that costs”                    */
    homePrice: number;

    /** core breakdown fields                                           */
    loanAmount: number;
    downPayment: number;
    totalMonthly: number;
    principalAndInterest: number;
    taxesAndInsurance: number;
    pmi: number;

    /** chart inputs                                                    */
    annualRate: number;
    loanTerm: number;

    /** util – turns numbers into “commas”                           */
    toCurrency: (value: number) => string;

    isOpen: boolean;
    setIsOpen: (v: boolean) => void;
}

const MortgageResult: React.FC<MortgageResultProps> = ({
    homePrice,
    loanAmount,
    downPayment,
    totalMonthly,
    principalAndInterest,
    taxesAndInsurance,
    pmi,
    annualRate,
    loanTerm,
    toCurrency,
    isOpen,
    setIsOpen,
}) => {
    const [tab, setTab] = useState<"breakdown" | "overtime" | "amortization">("breakdown");

    /* ─────────────────────────────────────────────────────────────── */

    return (
        <div className="border border-gray-300 shadow 2xl:w-[900px]">
            {/* Accordion Header */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full bg-[#1275ba] text-white px-4 py-3 flex items-center justify-between"
            >
                <h5 className="text-center font-bold flex-1 uppercase 2xl:text-[18px]">
                    You can afford a home that costs up&nbsp;to <span className="dtotal">{toCurrency(homePrice)}</span>
                </h5>
                <span className="text-xl">{isOpen ? "−" : "+"}</span>
            </button>

            {/* Accordion Body */}
            <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    isOpen ? "opacity-100" : "max-h-0 opacity-0"
                }`}
            >
                {/* 6‑column breakdown table */}
                <table
                    className="w-[320px] sm:w-full mx-auto mt-6  dcalcvals text-[15px] font-semibold border border-gray-300 border-b 2xl:mt-4 2xl:w-[874px] 2xl:mx-auto"
                    cellPadding={0}
                    cellSpacing={0}
                >
                    <thead className="sr-only">
                        <tr>
                            <th scope="col">Loan Amount</th>
                            <th scope="col">Down Payment</th>
                            <th scope="col">Total Monthly Payment</th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr className="even:bg-[#f3f9fd]">
                            <td className="border-l border-gray-300 py-2 text-center">
                                <strong>Loan Amount: </strong>{" "}
                                <span className="text-[#1275ba] text-[22px]">{toCurrency(loanAmount)}</span>
                            </td>
                            <td className="border-l border-gray-300 py-2 text-center">
                                <strong>Down Payment: </strong>{" "}
                                <span className="text-[#1275ba] text-[22px]">{toCurrency(downPayment)}</span>
                            </td>
                            <td className="border-l border-gray-300 py-2 text-center">
                                <strong>Total Monthly Payment: </strong>{" "}
                                <span className="text-[#1275ba] text-[22px]">{toCurrency(totalMonthly)}</span>
                            </td>
                        </tr>

                        {/* second (3‑cell) row – add another hidden header row first */}
                        <tr className="sr-only">
                            <th scope="col">Principal & Interest</th>
                            <th scope="col">PMI</th>
                            <th scope="col">Taxes & Insurance</th>
                        </tr>

                        <tr className="odd:bg-[#f3f9fd]">
                            <td className="border-l border-t border-gray-300 py-2 text-center">
                                <strong>Principal and Interest: </strong>{" "}
                                <span className="text-[#1275ba] text-[22px]">{toCurrency(principalAndInterest)}</span>
                            </td>
                            <td className="border-l border-t border-gray-300 py-2 text-center">
                                <strong>Private Mortgage Insurance&nbsp;(PMI): </strong>{" "}
                                <span className="text-[#1275ba] text-[22px]">{toCurrency(pmi)}</span>
                            </td>
                            <td className="border-l border-t border-gray-300 py-2 text-center">
                                <strong>Property Taxes and Insurance: </strong>{" "}
                                <span className="text-[#1275ba] text-[22px]">{toCurrency(taxesAndInsurance)}</span>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* headline (again) for desktop parity */}
                <div className="text-center pt-4">
                    <h2 className="text-[#212459] text-[24px] font-bold uppercase">
                        You can afford a home that costs up&nbsp;to{" "}
                        <span className="dtotal">{toCurrency(homePrice)}</span>
                    </h2>
                </div>

                {/* tab buttons */}
                <div className="flex flex-col items-center gap-1 2xl:flex-row 2xl:justify-center 2xl:gap-2 mt-4">
                    {["breakdown", "overtime", "amortization"].map((label) => (
                        <button
                            key={label}
                            onClick={() => setTab(label as typeof tab)}
                            className={`w-[180px] sm:w-full px-4 py-2 font-bold uppercase rounded 2xl:text-[18px]
                ${tab === label ? "bg-[#1275ba] text-white" : "bg-[#202358] text-white hover:bg-[#1275ba]"} 2xl:w-[180px]`}
                        >
                            {label.toUpperCase()}
                        </button>
                    ))}
                </div>

                {/* tab content */}
                <div className="mt-6 px-4 pb-4">
                    {tab === "breakdown" && (
                        <MortgagePieChart
                            principalAndInterest={principalAndInterest}
                            taxesAndInsurance={taxesAndInsurance}
                        />
                    )}

                    {tab === "overtime" && (
                        <MortgageOverTimeChart
                            principalAndInterest={principalAndInterest}
                            taxesAndInsurance={taxesAndInsurance}
                            pmi={pmi}
                        />
                    )}

                    {tab === "amortization" && (
                        <MortgageAmortizationTable
                            loanAmount={loanAmount}
                            annualRate={annualRate}
                            loanTerm={loanTerm}
                            toCurrency={toCurrency}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default MortgageResult;
